import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import { api } from "@/lib/api-client";
import { useWsClient } from "@/lib/ws-provider";
import { useAuth } from "@/hooks/useAuth";
import { ComposeNoteDialog } from "@/features/directed-notes/components/ComposeNoteDialog";
import { canSendDirectNote } from "@/features/directed-notes/lib/permissions";
import { EvictOperatorDialog } from "@/features/queue-evictions/components/EvictOperatorDialog";
import { canQueueEvict } from "@/features/queue-evictions/lib/permissions";
import "../admin.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StaffLiveEntry {
  operatorId: string;
  displayName: string;
  status: "active" | "idle";
  idleMs: number;
  currentKpm: number;
  currentDocId: string | null;
  docsToday: number;
}

interface OperatorMetricsTick {
  type: "operator_metrics_tick";
  operatorId: string;
  kpm5m: number;
  currentDocId: string;
  currentStep: string;
  at: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format idleMs as a human-readable string, e.g. "0s", "45s", "3m" */
function formatIdle(ms: number): string {
  if (ms <= 0) return "0s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m`;
}

const POLL_INTERVAL_MS = 15_000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * R-236 B2 — Live Roster
 *
 * Hybrid poll + WS approach:
 * - Fetches GET /api/ops/staff/live on mount and every 15 s (authoritative
 *   source for status, idleMs, docsToday, and roster membership).
 * - Subscribes to `operator_metrics_tick` via the shared WsClient for
 *   snappy live KPM + active-status updates between polls.
 *
 * Row click → sets ?tab=historical&operatorId=<id> in the URL, leaving B3
 * (HistoricalReport) to consume the operatorId param.
 */
export function LiveRoster() {
  const [operators, setOperators] = useState<StaffLiveEntry[] | null>(null);
  const wsClient = useWsClient();
  const [, setSearchParams] = useSearchParams();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Task 13 (WU-2.3) — cap-gated "Send note" row action. Affordance only;
  // server is authoritative on supervisor.direct_note.
  const { user } = useAuth();
  const canDirectNote = canSendDirectNote(user);
  const [noteTarget, setNoteTarget] = useState<{ id: string; name: string | null } | null>(null);
  // Task 9 (WU-2.4) — cap-gated "Evict…" row action, cloned from the Send-note
  // wiring above. Affordance only; server is authoritative on
  // supervisor.queue_evict.
  const canEvict = canQueueEvict(user);
  const [evictTarget, setEvictTarget] = useState<{ id: string; name: string | null } | null>(null);
  const showActionsColumn = canDirectNote || canEvict;
  // Guard overlapping in-flight fetches — the 15s poll could fire a second
  // fetch while the first is still pending, and a slow/stale response could
  // clobber newer data. Early-return when a fetch is already running.
  const isFetchingRef = useRef(false);

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchRoster = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const data = await api.get<{ operators: StaffLiveEntry[] }>("/ops/staff/live");
      setOperators(data.operators);
    } catch {
      // Non-fatal — keep stale data
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Initial fetch + periodic poll
  useEffect(() => {
    fetchRoster();
    pollRef.current = setInterval(fetchRoster, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchRoster]);

  // ── WS subscription ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!wsClient) return;

    const off = wsClient.on("operator_metrics_tick", (data: unknown) => {
      const msg = data as OperatorMetricsTick;
      if (!msg || typeof msg.operatorId !== "string") return;

      setOperators((prev) => {
        if (!prev) return prev;
        return prev.map((op) => {
          if (op.operatorId !== msg.operatorId) return op;
          // Tick means operator is actively keying → flip to active
          return {
            ...op,
            status: "active" as const,
            idleMs: 0,
            currentKpm: typeof msg.kpm5m === "number" ? msg.kpm5m : op.currentKpm,
            currentDocId: typeof msg.currentDocId === "string" ? msg.currentDocId : op.currentDocId,
          };
        });
      });
    });

    return off;
  }, [wsClient]);

  // ── Row click → Historical tab ──────────────────────────────────────────
  function handleRowSelect(operatorId: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "historical");
        next.set("operatorId", operatorId);
        return next;
      },
      { replace: false },
    );
  }

  // ── Render states ────────────────────────────────────────────────────────
  if (operators === null) {
    return (
      <div className="admin-loading" data-testid="roster-loading">
        <span>Loading…</span>
      </div>
    );
  }

  if (operators.length === 0) {
    return (
      <div className="jt-empty" data-testid="roster-empty">
        No operators online.
      </div>
    );
  }

  return (
    <div data-testid="live-roster">
      {noteTarget && (
        <ComposeNoteDialog
          presetRecipient={noteTarget}
          onClose={() => setNoteTarget(null)}
          onSent={() => setNoteTarget(null)}
        />
      )}
      {evictTarget && (
        <EvictOperatorDialog
          operator={evictTarget}
          onClose={() => setEvictTarget(null)}
          onCreated={() => setEvictTarget(null)}
        />
      )}
      <table className="roster-table">
        <thead>
          <tr>
            <th>Operator</th>
            <th>Status</th>
            <th>Idle</th>
            <th>KPM</th>
            <th>Current doc</th>
            <th>Docs today</th>
            {showActionsColumn && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {operators.map((op) => {
            const isActive = op.status === "active";
            return (
              <tr
                key={op.operatorId}
                data-testid={`roster-row-${op.operatorId}`}
                className="roster-row"
                role="button"
                tabIndex={0}
                onClick={() => handleRowSelect(op.operatorId)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return; // an inner control (Send note) owns its own keys
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowSelect(op.operatorId);
                  }
                }}
              >
                <td>{op.displayName}</td>
                <td data-testid={`roster-status-${op.operatorId}`}>
                  {/* Emoji is decorative; the sr-only label carries the
                      accessible name (WCAG 1.1.1/1.4.1 — don't rely on color
                      or glyph alone). */}
                  <span aria-hidden="true">{isActive ? "🟢" : "🟡"}</span>
                  <span className="sr-only">{isActive ? "Active" : "Idle"}</span>
                </td>
                <td data-testid={`roster-idle-${op.operatorId}`}>
                  {formatIdle(op.idleMs)}
                </td>
                <td data-testid={`roster-kpm-${op.operatorId}`}>{op.currentKpm}</td>
                <td data-testid={`roster-doc-${op.operatorId}`}>
                  {op.currentDocId ?? "—"}
                </td>
                <td data-testid={`roster-docs-today-${op.operatorId}`}>
                  {op.docsToday}
                </td>
                {showActionsColumn && (
                  <td>
                    {canDirectNote && (
                      <button
                        type="button"
                        className="jt-action-btn"
                        data-testid={`send-note-${op.operatorId}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setNoteTarget({ id: op.operatorId, name: op.displayName });
                        }}
                      >
                        Send note
                      </button>
                    )}
                    {canEvict && (
                      <button
                        type="button"
                        className="jt-action-btn"
                        data-testid={`evict-${op.operatorId}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEvictTarget({ id: op.operatorId, name: op.displayName });
                        }}
                      >
                        Evict…
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
